import styles from './ContentSetList.module.css'
import { statusMetaFor, productVersionLabel, productVersionTitle, deleteAffordance } from './sourceVisuals'
import { formatDate } from '../utils/formatDate'
import Icon from './Icon'

/**
 * ContentSetList — a document's per-product-version content-sets, each with its
 * own ingest status, page count and delete action. Used both in the expanded
 * table row and in the source details modal, so the two always describe a
 * content-set the same way.
 *
 * @param {object} props
 * @param {Array<{productVersion: string, status: string, pageCount: number|null, errorReason: string, processedAt: string|null}>} [props.contentSets]
 * @param {function(object): void} [props.onDelete] — omit to render read-only
 * @param {string} [props.emptyLabel]
 */
export default function ContentSetList({ contentSets = [], onDelete, emptyLabel = 'No content yet.' }) {
  if (!contentSets.length) {
    return <div className={styles.empty}>{emptyLabel}</div>
  }

  return (
    <div className={styles.panel}>
      {/* Its own column labels rather than borrowed ones: what a version reports
          doesn't line up with the columns of whatever list it's nested under. */}
      <div className={styles.header}>
        <span>Version</span>
        <span>Pages</span>
        <span>Status</span>
        <span>Updated</span>
        <span />
      </div>

      <ul className={styles.list}>
        {contentSets.map(cs => {
          const statusMeta = statusMetaFor(cs.status)
          const { blocked, retry } = deleteAffordance([cs])
          return (
            <li key={cs.productVersion} className={styles.item}>
              <span className={styles.version} title={productVersionTitle(cs.productVersion)}>
                {productVersionLabel(cs.productVersion)}
              </span>
              <span className={styles.pages}>{cs.pageCount ? `${cs.pageCount} pages` : '—'}</span>
              <span className={styles.status}>
                <span className={`${styles.statusPill} ${styles[statusMeta.className]}`}>
                  {statusMeta.label}
                </span>
              </span>
              <span className={styles.processed}>{formatDate(cs.processedAt)}</span>
              {/* Rendered even with no delete on offer, so the grid keeps the same
                  column count either way and read-only lists stay aligned with
                  editable ones. */}
              <span className={styles.actions}>
                {onDelete && (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    aria-label={`${retry ? 'Retry deleting' : 'Delete'} version ${productVersionLabel(cs.productVersion)}`}
                    title={blocked
                      ? `Can't delete a version that's ${statusMeta.label.toLowerCase()}`
                      : retry
                        ? "Deletion of this version hasn't finished — retry it"
                        : 'Delete this version'}
                    disabled={blocked}
                    onClick={e => { e.stopPropagation(); onDelete(cs) }}
                  >
                    <Icon name="trash" />
                  </button>
                )}
              </span>
              {cs.status === 'error' && cs.errorReason && (
                <span className={styles.errorReason}>{cs.errorReason}</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
